import Swal from "sweetalert2";
import { t } from "i18next";
import { const_routes } from "../helpers/const_routes";
import { fetchToken } from "../helpers/fetch";
import { types } from "../types/types";
import { startLogout } from "./authActions";

const handleTokenError = (body, dispatch) => {
  if (body?.code === "bak-error-001") {
    Swal.fire(t("titles.error"), `${t("alerts.validation.bak-error-001")} - ${body.method || ""}`, "error");
    return true;
  }
  if (body?.code === "bak-aut-tok-inv-001") {
    Swal.fire(t("titles.error"), t("api.token.invalid_petition"), "error");
    dispatch(startLogout());
    return true;
  }
  if (body?.code === "bak-aut-tok-inv-002") {
    Swal.fire(t("titles.error"), t("alerts.validation.bak-aut-tok-inv-002"), "error");
    dispatch(startLogout());
    return true;
  }
  return false;
};

export const startLoadingSearchKeyTerms = (filters = {}) => async (dispatch) => {
  dispatch({ type: types.SEARCH_KEY_TERM_SET_LOADING, payload: true });
  const params = new URLSearchParams();
  if (filters.catalogmoduleId) {
    params.set("catalogmoduleId", filters.catalogmoduleId);
  }
  if (filters.status) {
    params.set("status", filters.status);
  }
  if (filters.text) {
    params.set("text", filters.text);
  }
  const query = params.toString();
  const endpoint = query ? `get?${query}` : "get";
  const resp = await fetchToken(
    const_routes.SCHEMA,
    const_routes.VERSIONV1,
    const_routes.ROUTE_SEARCH_KEY_TERM,
    endpoint
  );
  const body = await resp.json();
  if (body.ok) {
    dispatch({
      type: types.SEARCH_KEY_TERM_SET_ROWS,
      payload: {
        rows: body.rows || [],
        titleEnabled: Boolean(body.titleEnabled)
      }
    });
    return;
  }
  dispatch({ type: types.SEARCH_KEY_TERM_SET_LOADING, payload: false });
  if (!handleTokenError(body, dispatch) && body.msg) {
    Swal.fire(t("titles.error"), body.msg, "error");
  }
};

export const fetchAssignedSearchKeyTerms = (catalogmoduleId) => async (dispatch) => {
  const params = new URLSearchParams({
    catalogmoduleId: String(catalogmoduleId),
    mine: "1",
    status: "assigned"
  });
  const resp = await fetchToken(
    const_routes.SCHEMA,
    const_routes.VERSIONV1,
    const_routes.ROUTE_SEARCH_KEY_TERM,
    `get?${params.toString()}`
  );
  const body = await resp.json();
  if (body.ok) {
    return body.rows || [];
  }
  if (!handleTokenError(body, dispatch) && body.msg) {
    Swal.fire(t("titles.error"), body.msg, "error");
  }
  return [];
};

export const startLoadingSearchTermFields = (catalogmoduleId) => async (dispatch) => {
  const resp = await fetchToken(
    const_routes.SCHEMA,
    const_routes.VERSIONV1,
    const_routes.ROUTE_SEARCH_KEY_TERM,
    `fields/${catalogmoduleId}`
  );
  const body = await resp.json();
  if (body.ok) {
    dispatch({
      type: types.SEARCH_KEY_TERM_SET_FIELDS,
      payload: {
        rows: body.rows || [],
        openTextEnabled: Boolean(body.openTextEnabled),
        titleEnabled: Boolean(body.titleEnabled),
        briefEnabled: Boolean(body.briefEnabled)
      }
    });
    return body;
  }
  handleTokenError(body, dispatch);
  return null;
};

export const startLoadingSearchKeyTerm = (id) => async (dispatch) => {
  const resp = await fetchToken(
    const_routes.SCHEMA,
    const_routes.VERSIONV1,
    const_routes.ROUTE_SEARCH_KEY_TERM,
    `get/${id}`
  );
  const body = await resp.json();
  if (body.ok) {
    dispatch({
      type: types.SEARCH_KEY_TERM_SET_SELECTED,
      payload: { row: body.row, labels: body.labels || {} }
    });
    return body;
  }
  handleTokenError(body, dispatch);
  return null;
};

export const startSavingSearchKeyTerm = (payload, id) => async (dispatch) => {
  dispatch({ type: types.SEARCH_KEY_TERM_SET_SAVING, payload: true });
  const resp = await fetchToken(
    const_routes.SCHEMA,
    const_routes.VERSIONV1,
    const_routes.ROUTE_SEARCH_KEY_TERM,
    id ? `update/${id}` : "add",
    payload,
    id ? "PUT" : "POST"
  );
  const body = await resp.json();
  dispatch({ type: types.SEARCH_KEY_TERM_SET_SAVING, payload: false });
  if (body.ok) {
    Swal.fire(
      t("titles.success"),
      t("specialist.feeder.term.saved", "Término clave guardado."),
      "success"
    );
    return body.row;
  }
  if (!handleTokenError(body, dispatch)) {
    Swal.fire(t("titles.error"), body.msg || t("alerts.validation.bak-error-001"), "error");
  }
  return null;
};

export const startSettingSearchKeyTermStatus = (id, status, roleName) => async (dispatch) => {
  const resp = await fetchToken(
    const_routes.SCHEMA,
    const_routes.VERSIONV1,
    const_routes.ROUTE_SEARCH_KEY_TERM,
    `lifecycle/${id}`,
    { status, role_name: roleName },
    "PUT"
  );
  const body = await resp.json();
  if (body.ok) {
    return body.row;
  }
  if (!handleTokenError(body, dispatch)) {
    Swal.fire(t("titles.error"), body.msg || t("alerts.validation.bak-error-001"), "error");
  }
  return null;
};

export const startDiscardingSearchKeyTerm = (id) => async (dispatch) => {
  const resp = await fetchToken(
    const_routes.SCHEMA,
    const_routes.VERSIONV1,
    const_routes.ROUTE_SEARCH_KEY_TERM,
    `discard/${id}`,
    {},
    "PUT"
  );
  const body = await resp.json();
  if (body.ok) {
    return true;
  }
  if (!handleTokenError(body, dispatch)) {
    Swal.fire(t("titles.error"), body.msg || t("alerts.validation.bak-error-001"), "error");
  }
  return false;
};

const foldText = (value) => String(value || "")
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "")
  .toLowerCase();

const optionLabel = (row) => {
  const code = row.code_visual || row.code || "";
  const name = row.name || "";
  return code ? `${code} — ${name}` : name;
};

const optionCache = {};

const loadOnce = (key, loader) => {
  if (!optionCache[key]) {
    optionCache[key] = loader();
  }
  return optionCache[key];
};

const filterOptions = (rows, query) => {
  const needle = foldText(query);
  return rows
    .filter((row) => !needle || foldText(row.label).includes(needle))
    .slice(0, 20);
};

export const searchCatalogOptions = async (sourceKind, text, catalogtypeId, topicId) => {
  const query = String(text || "").trim();
  if (sourceKind === "ciiu") {
    const rows = await loadOnce("ciiu", async () => {
      const resp = await fetchToken(
        const_routes.SCHEMA,
        const_routes.VERSIONV1,
        const_routes.ROUTE_SEARCH_KEY_TERM,
        "ciiu-catalog"
      );
      const body = await resp.json();
      return (body.rows || []).map((row) => ({
        id: row.id,
        label: row.code ? `${row.code} — ${row.name}` : row.name,
        level: row.level
      }));
    });
    return filterOptions(rows, query);
  }

  if (sourceKind === "section") {
    const rows = await loadOnce("section", async () => {
      const resp = await fetchToken(const_routes.SCHEMA, const_routes.VERSIONV1, const_routes.ROUTE_CATALOGSECTION, "get");
      const body = await resp.json();
      const list = Array.isArray(body.catalogSections) ? body.catalogSections : [];
      return list.map((row) => ({ id: row.id, label: optionLabel(row) }));
    });
    return filterOptions(rows, query);
  }

  if (sourceKind === "country") {
    const rows = await loadOnce("country", async () => {
      const resp = await fetchToken(const_routes.SCHEMA, const_routes.VERSIONV1, const_routes.ROUTE_COUNTRY, "get");
      const body = await resp.json();
      return (body.countrys || []).map((row) => ({ id: row.id, label: row.name }));
    });
    return filterOptions(rows, query);
  }

  if (sourceKind === "topic") {
    const rows = await loadOnce("topic", async () => {
      const resp = await fetchToken(const_routes.SCHEMA, const_routes.VERSIONV1, const_routes.ROUTE_CATALOG_TOPIC, "get");
      const body = await resp.json();
      return (body.rows || []).map((row) => ({ id: row.id, label: row.name }));
    });
    return filterOptions(rows, query);
  }

  if (sourceKind === "question") {
    if (!topicId) {
      return [];
    }
    const rows = await loadOnce(`question-${topicId}`, async () => {
      const resp = await fetchToken(
        const_routes.SCHEMA,
        const_routes.VERSIONV1,
        const_routes.ROUTE_SEARCH_KEY_TERM,
        `questions?topicId=${topicId}`
      );
      const body = await resp.json();
      return (body.rows || []).map((row) => ({ id: row.id, label: row.name }));
    });
    return filterOptions(rows, query);
  }

  if (!catalogtypeId) {
    return [];
  }
  const rows = await loadOnce(`catalog-${catalogtypeId}`, async () => {
    const resp = await fetchToken(
      const_routes.SCHEMA,
      const_routes.VERSIONV1,
      const_routes.ROUTE_CATALOG,
      `getByCatalogTypeId/${catalogtypeId}`
    );
    const body = await resp.json();
    return (body.catalogs || []).map((row) => ({ id: row.id, label: String(row.description || '').trim() || row.name }));
  });
  return filterOptions(rows, query);
};
